<script setup lang="ts">
import RlUploadMultiple from '@/components/common/upload/RlUploadMultiple.vue'
import { createEnergyBillingEstimation, updateEnergyBillingEstimation } from '@/services/eos/billing-estimates.service'
import type { LiteSite } from '@/types/core/Site'
import type { IBillEstimationDashboardRow } from '@/types/eos/BillEstimation'
import type { IBillFee, ICreateEnergyBillEstimationForm, IUpdateEnergyBillEstimationForm } from '@/types/eos/forms'
import { Delete } from '@element-plus/icons-vue'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { ElMessage, type FormInstance, type FormRules, type UploadFile } from 'element-plus'
import { DateTime } from 'luxon'
import { computed, reactive, ref, watch } from 'vue'

const props = defineProps<{
  sites: LiteSite[]
  defaultSite?: string
  editData?: IBillEstimationDashboardRow
}>()

const feesOptions = [
  {
    label: 'Adder',
    value: 'Adder',
  },
  {
    label: 'Capacity',
    value: 'Capacity',
  },
  {
    label: 'Energy Volume',
    value: 'Energy Volume',
  },
  {
    label: 'RECs',
    value: 'RECs',
  },
  {
    label: 'Scheduling Fee',
    value: 'Scheduling Fee',
  },
  {
    label: 'Taxes',
    value: 'Taxes',
  },
  {
    label: 'Transmission Charge',
    value: 'Transmission Charge',
  },
  {
    label: 'Transmission TrueUp',
    value: 'Transmission TrueUp',
  },
]

const customFees = ref<boolean>(false)
const submitLoading = ref<boolean>(false)

const model = defineModel<boolean>()
const createBillingEstimationFormRef = ref<FormInstance>()
const deletedAttachmentIds = ref<number[]>([])

const createBillingEstimationForm = reactive<ICreateEnergyBillEstimationForm>({
  site: '',
  period: DateTime.now().minus({ month: 1 }).toJSDate(),
  actual_value: undefined,
  ncp_demand: undefined,
  meter_prior: undefined,
  meter_current: undefined,
  fees: Array<IBillFee>(),
  attachments: Array<UploadFile>(),
})

const rules = reactive<FormRules<ICreateEnergyBillEstimationForm>>({
  site: [{ required: true, message: 'Site is required.' }],
  actual_value: [{ required: true, message: 'Final amount is required.', trigger: ['blur'] }],
  period: [
    { required: true, message: 'Period is required.' },
    {
      validator: (rule, value, callback) => {
        // Validate period is not in the future
        const today = new Date()

        if (
          createBillingEstimationForm.period.getFullYear() >= today.getFullYear() &&
          createBillingEstimationForm.period.getMonth() >= today.getMonth()
        ) {
          return callback(new Error('Period cannot be in the future'))
        }

        callback()
      },
      trigger: 'blur',
    },
  ],
  ncp_demand: [
    {
      type: 'pattern',
      pattern: /^\d+(\.\d+)?$/,
      message: 'Must be numeric',
      trigger: 'blur',
    },
  ],
  meter_prior: [
    {
      type: 'pattern',
      pattern: /^\d+$/,
      message: 'Must be numeric without decimals',
      trigger: 'blur',
    },
  ],
  meter_current: [
    {
      type: 'pattern',
      pattern: /^\d+$/,
      message: 'Must be numeric without decimals',
      trigger: 'blur',
    },
  ],
})

const queryClient = useQueryClient()

const isEditing = computed(() => !!props.editData)

// Watch for attachment removals and track deleted IDs
watch(
  () => createBillingEstimationForm.attachments,
  (newAttachments: UploadFile[], oldAttachments?: UploadFile[]) => {
    if (!isEditing.value || !oldAttachments) return

    // Find attachments that were removed (had uid but are no longer in the array)
    const currentUids = new Set(
      newAttachments.filter((file: UploadFile) => file.uid).map((file: UploadFile) => Number(file.uid))
    )
    const oldUids = new Set(
      oldAttachments.filter((file: UploadFile) => file.uid).map((file: UploadFile) => Number(file.uid))
    )

    // Find removed UIDs
    const removedUids = Array.from(oldUids).filter((uid) => !currentUids.has(uid))

    // Add removed UIDs to deleted_attachment_ids if not already present
    removedUids.forEach((uid) => {
      if (!deletedAttachmentIds.value.includes(uid)) {
        deletedAttachmentIds.value.push(uid)
      }
    })

    // Remove UIDs that were re-added (if a file was removed and then re-added)
    const reAddedUids = Array.from(currentUids).filter((uid) => !oldUids.has(uid))
    reAddedUids.forEach((uid) => {
      const index = deletedAttachmentIds.value.indexOf(uid)
      if (index !== -1) {
        deletedAttachmentIds.value.splice(index, 1)
      }
    })
  },
  { deep: true }
)

const create = useMutation({
  mutationFn: (form: ICreateEnergyBillEstimationForm) => createEnergyBillingEstimation(form),
  onSuccess: () => queryClient.resetQueries({ queryKey: ['bill-estimations'] }),
})

const update = useMutation({
  mutationFn: (form: IUpdateEnergyBillEstimationForm) => updateEnergyBillingEstimation(form),
  onSuccess: () => queryClient.resetQueries({ queryKey: ['bill-estimations'] }),
})

/**
 * Handles form submit.
 *
 * @param formEl form element ref.
 */
const submitForm = async (formEl: FormInstance | undefined) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  formEl?.validate(async (valid, fields) => {
    if (valid) {
      try {
        submitLoading.value = true

        if (isEditing.value && props.editData) {
          await update.mutateAsync({
            id: props.editData.id,
            ...createBillingEstimationForm,
            deleted_attachment_ids: deletedAttachmentIds.value,
          })
          ElMessage({
            type: 'success',
            message: 'Bill updated successfully',
          })
        } else {
          await create.mutateAsync(createBillingEstimationForm)
          ElMessage({
            type: 'success',
            message: 'Bill created successfully',
          })
        }

        resetFormCloseDialog()
      } catch (err: any) {
        ElMessage({
          type: 'error',
          message: `Operation failed: ${err.message}`,
        })
      } finally {
        submitLoading.value = false
      }
    }
  })
}

/**
 * Adds a fee to the form.
 */
const addFee = () => {
  customFees.value = true
  createBillingEstimationForm.fees.push({
    key: Date.now(),
    name: undefined,
    amount: undefined,
  })
}

/**
 * Removes a fee from the form.
 *
 * @param item the fee item to remove.
 */
const removeFee = (item: IBillFee) => {
  const index = createBillingEstimationForm.fees.indexOf(item)
  if (index !== -1) {
    createBillingEstimationForm.fees.splice(index, 1)
  }
}

/**
 * Converts existing attachments to UploadFile format
 */
const convertAttachmentsToUploadFiles = (attachments: IBillEstimationDashboardRow['attachments']): UploadFile[] => {
  return attachments.map((attachment) => ({
    name: attachment.name,
    url: attachment.file_url,
    uid: attachment.id,
    status: 'success',
  }))
}

const initForm = async () => {
  if (props.editData) {
    // Populate form with existing data
    createBillingEstimationForm.site = props.editData.site.site_id
    createBillingEstimationForm.period = new Date(props.editData.year, props.editData.month - 1)
    createBillingEstimationForm.actual_value = props.editData.actual_value
      ? parseFloat(props.editData.actual_value)
      : undefined
    createBillingEstimationForm.ncp_demand = props.editData.ncp_demand
      ? parseFloat(props.editData.ncp_demand)
      : undefined
    createBillingEstimationForm.meter_prior = props.editData.meter_prior
      ? parseFloat(props.editData.meter_prior)
      : undefined
    createBillingEstimationForm.meter_current = props.editData.meter_current
      ? parseFloat(props.editData.meter_current)
      : undefined

    // Populate fees from actual_value_fees
    if (props.editData.actual_value_fees && props.editData.actual_value_fees.length > 0) {
      customFees.value = true
      createBillingEstimationForm.fees = props.editData.actual_value_fees.map((fee) => ({
        key: Date.now() + Math.random(),
        name: fee.name,
        amount: parseFloat(fee.amount),
      }))
    }

    // Populate attachments
    if (props.editData.attachments && props.editData.attachments.length > 0) {
      createBillingEstimationForm.attachments = convertAttachmentsToUploadFiles(props.editData.attachments)
    }
  } else {
    createBillingEstimationForm.site = props.defaultSite ?? ''
  }
}

/**
 * Resets form, closes dialog.
 */
const resetFormCloseDialog = () => {
  resetForm(createBillingEstimationFormRef.value)
  model.value = false
}

/**
 * Resets the form.
 *
 * @param formEl form element ref.
 */
const resetForm = (formEl: FormInstance | undefined) => {
  formEl?.resetFields()
  createBillingEstimationForm.fees = []
  createBillingEstimationForm.attachments = []
  deletedAttachmentIds.value = []
  customFees.value = false
}
</script>

<template>
  <el-dialog
    v-model="model"
    :title="isEditing ? 'Edit bill details' : 'Add bill details'"
    :show-close="false"
    :width="480"
    @open="initForm"
    @close="resetForm(createBillingEstimationFormRef)"
  >
    <el-form
      ref="createBillingEstimationFormRef"
      :model="createBillingEstimationForm"
      :rules="rules"
      label-width="auto"
      label-position="left"
    >
      <el-form-item label="Site" prop="site">
        <el-select v-model="createBillingEstimationForm.site" placeholder="Site" clearable>
          <el-option v-for="s in props.sites" :key="s.site_name" :label="s.site_name" :value="s.site_id" />
        </el-select>
      </el-form-item>
      <el-form-item label="Period" prop="period">
        <el-date-picker
          v-model="createBillingEstimationForm.period"
          type="month"
          placeholder="Select month"
          format="MMMM YYYY"
        ></el-date-picker>
      </el-form-item>
      <el-form-item label="Attachments" prop="attachments">
        <RlUploadMultiple v-model="createBillingEstimationForm.attachments" :limit="5" />
      </el-form-item>
      <el-form-item label="NCP Demand" prop="ncp_demand">
        <el-input v-model="createBillingEstimationForm.ncp_demand" clearable />
      </el-form-item>
      <el-form-item label="Meter Prior" prop="meter_prior">
        <el-input v-model="createBillingEstimationForm.meter_prior" clearable />
      </el-form-item>
      <el-form-item label="Meter Current" prop="meter_current">
        <el-input v-model="createBillingEstimationForm.meter_current" clearable />
      </el-form-item>
      <el-form-item label="Final Amount" prop="actual_value">
        <el-currency-input v-model="createBillingEstimationForm.actual_value" clearable />
      </el-form-item>
      <el-form-item v-for="(fee, index) in createBillingEstimationForm.fees" :key="fee.key">
        <div class="fee-group">
          <el-form-item
            :key="`${fee.key}.fee_name`"
            class="fee-group__fee-select"
            :prop="`fees.${index}.name`"
            :rules="{ required: customFees, message: 'Fee name required.', trigger: ['change'] }"
          >
            <el-select-v2
              v-model="fee.name"
              placeholder="Select a fee"
              :options="feesOptions"
              filterable
              clearable
              allow-create
            />
          </el-form-item>
          <el-form-item
            :key="`${fee.key}.fee_amount`"
            :prop="`fees.${index}.amount`"
            :rules="{ required: customFees, message: 'Amount required.', trigger: ['blur'] }"
          >
            <el-currency-input v-model="fee.amount" class="fee-group__amount" clearable />
          </el-form-item>
          <el-button v-if="createBillingEstimationForm.fees.length >= 1" link :icon="Delete" @click="removeFee(fee)" />
        </div>
      </el-form-item>
      <el-button class="add-fee-button" link type="primary" @click="addFee">+ Add a custom fee</el-button>
    </el-form>
    <template #footer>
      <el-button @click="resetFormCloseDialog">Cancel</el-button>
      <el-button type="primary" :loading="submitLoading" @click="submitForm(createBillingEstimationFormRef)">
        Submit
      </el-button>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.fee-group {
  width: 100%;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-spacing-xs);

  &__amount {
    width: 160px;
  }

  &__fee-select {
    flex-grow: 1;
  }
}
</style>
